import { Pencil, Plus, Search, Trash } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderSelectModalExample from "../../components/HeaderSelectModal";
import MakeDefaultModal from "../../components/MakeDefaultModal";
import UnselectDefaultModal from "../../components/UnselectDefaultModal";
import { setGlobalPopupElement } from "../../features/popupSlice";
import { CustomHeaderItem } from "../../types/header";
import { toastMe } from "../../utils";
import {
	createCustomHeader,
	deleteCustomHeader,
	getAllCustomHeaders,
	makeHeaderDefault,
	unselectDefaultHeader,
} from "../../utils/requests";

// Import the new modal component

const HeaderManagementPage: React.FC = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();

	// State management with proper types
	const [headers, setHeaders] = useState<CustomHeaderItem[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [newHeaderName, setNewHeaderName] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	// Add state for the unselect modal and loading
	const [unselectModalOpen, setUnselectModalOpen] = useState<boolean>(false);
	const [headerToUnselect, setHeaderToUnselect] =
		useState<CustomHeaderItem | null>(null);
	const [unselectingHeaderId, setUnselectingHeaderId] = useState<
		string | null
	>(null);

	// Pagination settings
	const itemsPerPage: number = 5;
	const headerEditPath = location.pathname?.includes("/v2")
		? "/header-builder/v2/"
		: "/header-builder/";

	const loadHeaders = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await getAllCustomHeaders({
				perPage: itemsPerPage,
				search: searchTerm,
				page: currentPage,
			});
			setHeaders(response.data);
			setTotalPages(Math.ceil(response.page?.totalIndex / itemsPerPage));
		} catch (error) {
			console.error("Error fetching headers:", error);
		} finally {
			setIsLoading(false);
		}
	}, [searchTerm, currentPage]);

	// Load headers effect
	useEffect(() => {
		loadHeaders();
	}, [loadHeaders]);

	// Handle create new header
	const handleCreateHeader = (e: React.FormEvent) => {
		e.preventDefault();
		if (!newHeaderName.trim()) return;

		const createHeaderFunc = async (
			header?: CustomHeaderItem
		): Promise<void> => {
			setIsSubmitting(true);

			try {
				const newHeader = await createCustomHeader({
					name: newHeaderName,
					settings: header?.settings,
				});

				// In a real app, redirect to edit page would happen here
				// For demo, just add to the list
				setHeaders((prevHeaders) => [...prevHeaders, newHeader.data]);
				setNewHeaderName("");

				// Show success message (in real app, redirect here)
				toastMe(
					"Header created! You would be redirected to edit page.",
					"success"
				);

				navigate(`${headerEditPath}${newHeader.data._id}`);
			} catch (error) {
				console.error("Error creating header:", error);
				toastMe("Failed to create header. Please try again.", "error");
				setIsSubmitting(false);
			}
		};

		dispatch(
			setGlobalPopupElement(
				<HeaderSelectModalExample
					onSelect={(header) => {
						createHeaderFunc(header);
						dispatch(setGlobalPopupElement(null));
					}}
					onSkip={() => {
						createHeaderFunc();
						dispatch(setGlobalPopupElement(null));
					}}
				/>
			)
		);
	};

	// Handle make default
	const handleMakeDefault = (header: CustomHeaderItem): void => {
		// Logic to make header default
		dispatch(
			setGlobalPopupElement(
				<MakeDefaultModal
					isOpen={true}
					componentData={header}
					componentType="header"
					onClose={() => {
						dispatch(setGlobalPopupElement(null));
					}}
					onConfirm={async (id, screenMode) => {
						try {
							await makeHeaderDefault(id, screenMode);
							// Update the state to reflect the change
							loadHeaders();

							toastMe("Header set as default!", "success");
							dispatch(setGlobalPopupElement(null));
						} catch (error) {
							console.error(
								"Error making header default:",
								error
							);
							toastMe(
								"Failed to make header default. Please try again.",
								"error"
							);
						}
					}}
				/>
			)
		);
	};
	// Add a handler for unselecting default header
	const handleUnselectDefault = async (id: string): Promise<void> => {
		setUnselectingHeaderId(id);
		try {
			await unselectDefaultHeader(id);
			// Update the state to reflect the change
			loadHeaders();

			toastMe("Header removed from default!", "success");
		} catch (error) {
			console.error("Error unselecting default header:", error);
			toastMe(
				"Failed to remove header from default. Please try again.",
				"error"
			);
		} finally {
			setUnselectingHeaderId(null);
		}
	};

	// Add a function to open the unselect modal
	const openUnselectModal = (header: CustomHeaderItem) => {
		setHeaderToUnselect(header);
		setUnselectModalOpen(true);
	};

	// Handle delete
	const handleDelete = async (id: string): Promise<void> => {
		// eslint-disable-next-line no-restricted-globals
		if (confirm("Are you sure you want to delete this header?")) {
			try {
				await deleteCustomHeader(id);
				setHeaders((prevHeaders) =>
					prevHeaders.filter((header) => header._id !== id)
				);
			} catch (error) {
				console.error("Error deleting header:", error);
				toastMe("Failed to delete header. Please try again.", "error");
			}
		}
	};

	// Handle edit - in a real app this would navigate to edit page
	const handleEdit = (id: string): void => {
		navigate(`${headerEditPath}${id}`);
	};

	return (
		<div className="p-6 max-w-[1440px] mx-auto">
			<h1 className="text-2xl font-bold mb-6">Header Management</h1>

			{/* Search Panel */}
			<div className="bg-gray-100 p-4 mb-6 rounded-lg">
				<div className="flex items-center">
					<div className="relative flex-grow">
						<div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
							<Search className="w-5 h-5 text-gray-400" />
						</div>
						<input
							type="text"
							className="bg-white border border-gray-300 p-2 pl-10 rounded-md w-full"
							placeholder="Search headers by name or description..."
							value={searchTerm}
							onChange={(
								e: React.ChangeEvent<HTMLInputElement>
							) => setSearchTerm(e.target.value)}
						/>
					</div>
				</div>
			</div>

			{/* Create New Header Form */}
			<div className="bg-white p-4 mb-6 border rounded-lg shadow-sm">
				<h2 className="text-lg font-semibold mb-3">
					Create New Header
				</h2>
				<form
					onSubmit={handleCreateHeader}
					className="flex items-end gap-3">
					<div className="flex-grow">
						<label
							htmlFor="headerName"
							className="block text-sm font-medium mb-1">
							Header Name
						</label>
						<input
							type="text"
							id="headerName"
							className="bg-white border border-gray-300 p-2 rounded-md w-full"
							value={newHeaderName}
							onChange={(
								e: React.ChangeEvent<HTMLInputElement>
							) => setNewHeaderName(e.target.value)}
							required
						/>
					</div>
					<button
						type="submit"
						className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center"
						disabled={isSubmitting}>
						<Plus className="w-4 h-4 mr-1" />
						{isSubmitting ? "Creating..." : "Create"}
					</button>
				</form>
			</div>

			{/* Headers List */}
			<div className="bg-white border rounded-lg shadow-sm overflow-hidden">
				<h2 className="sr-only">Headers List</h2>
				{isLoading ? (
					<div className="p-8 text-center">Loading headers...</div>
				) : headers.length === 0 ? (
					<div className="p-8 text-center">
						No headers found. Create a new one!
					</div>
				) : (
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Header
								</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Description
								</th>
								<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
									Actions
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-200">
							{headers.map((header) => (
								<tr key={header._id}>
									<td className="px-6 py-4 whitespace-nowrap">
										<div className="flex items-center">
											{header.images ? (
												<img
													src={header.images}
													alt={header.name}
													className="w-10 h-10 rounded-md mr-3 bg-gray-200"
												/>
											) : (
												<div className="w-10 h-10 rounded-md mr-3 bg-gray-200"></div>
											)}
											<div className="flex items-center">
												<span className="font-medium">
													{header.name}
												</span>
												{header.isFrontHeader && (
													<span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 capitalize">
														Default:{" "}
														{header.screenMode ===
														"all"
															? "Desktop & Mobile"
															: header.screenMode}
													</span>
												)}
											</div>
										</div>
									</td>
									<td className="px-6 py-4">
										<span className="text-sm text-gray-600">
											{header.description}
										</span>
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
										<div className="flex justify-end space-x-2">
											{header.isFrontHeader ? (
												<button
													onClick={() =>
														openUnselectModal(
															header
														)
													}
													disabled={
														unselectingHeaderId ===
														header._id
													}
													className={`px-2 py-1 border border-red-600 text-red-600 rounded-xl flex items-center ${
														unselectingHeaderId ===
														header._id
															? "opacity-50 cursor-not-allowed"
															: ""
													}`}>
													{unselectingHeaderId ===
													header._id ? (
														<>
															<svg
																className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-600"
																xmlns="http://www.w3.org/2000/svg"
																fill="none"
																viewBox="0 0 24 24">
																<circle
																	className="opacity-25"
																	cx="12"
																	cy="12"
																	r="10"
																	stroke="currentColor"
																	strokeWidth="4"></circle>
																<path
																	className="opacity-75"
																	fill="currentColor"
																	d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
															</svg>
															Removing...
														</>
													) : (
														"Remove as Default"
													)}
												</button>
											) : (
												<button
													onClick={() =>
														handleMakeDefault(
															header
														)
													}
													className="px-2 py-1 border border-blue-600 text-blue-600 rounded-xl">
													Set as Default Header
												</button>
											)}

											<button
												onClick={() =>
													handleDelete(header._id)
												}
												className="text-red-600 hover:text-red-900"
												title="Delete"
												disabled={header.isFrontHeader}>
												<Trash
													className="w-5 h-5"
													style={{
														opacity:
															header.isFrontHeader
																? 0.5
																: 1,
													}}
												/>
											</button>

											<button
												onClick={() =>
													handleEdit(header._id)
												}
												className="text-blue-600 hover:text-blue-900"
												title="Edit">
												<Pencil className="w-5 h-5" />
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}

				{/* Pagination controls */}
				{totalPages > 1 && (
					<div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
						<div className="flex-1 flex justify-between sm:hidden">
							<button
								onClick={() =>
									setCurrentPage((prev) =>
										Math.max(prev - 1, 1)
									)
								}
								disabled={currentPage === 1}
								className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
								Previous
							</button>
							<button
								onClick={() =>
									setCurrentPage((prev) =>
										Math.min(prev + 1, totalPages)
									)
								}
								disabled={currentPage === totalPages}
								className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
								Next
							</button>
						</div>
						<div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
							<div>
								<p className="text-sm text-gray-700">
									Showing{" "}
									<span className="font-medium">
										{(currentPage - 1) * itemsPerPage + 1}
									</span>{" "}
									to{" "}
									<span className="font-medium">
										{Math.min(
											currentPage * itemsPerPage,
											headers.length
										)}
									</span>{" "}
									of{" "}
									<span className="font-medium">
										{headers.length}
									</span>{" "}
									results
								</p>
							</div>
							<div>
								<nav
									className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
									aria-label="Pagination">
									<button
										onClick={() =>
											setCurrentPage((prev) =>
												Math.max(prev - 1, 1)
											)
										}
										disabled={currentPage === 1}
										className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
										<span className="sr-only">
											Previous
										</span>
										&larr;
									</button>

									{Array.from(
										{ length: totalPages },
										(_, i) => i + 1
									).map((page) => (
										<button
											key={page}
											onClick={() => setCurrentPage(page)}
											className={`relative inline-flex items-center px-4 py-2 border ${
												currentPage === page
													? "bg-blue-50 border-blue-500 text-blue-600"
													: "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
											} text-sm font-medium`}>
											{page}
										</button>
									))}

									<button
										onClick={() =>
											setCurrentPage((prev) =>
												Math.min(prev + 1, totalPages)
											)
										}
										disabled={currentPage === totalPages}
										className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
										<span className="sr-only">Next</span>
										&rarr;
									</button>
								</nav>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Add the UnselectDefaultModal component */}
			<UnselectDefaultModal
				isOpen={unselectModalOpen}
				onClose={() => setUnselectModalOpen(false)}
				onConfirm={handleUnselectDefault}
				headerData={headerToUnselect}
			/>
		</div>
	);
};

export default HeaderManagementPage;
